import { ShieldAlert, Zap, Users } from "lucide-react";
import { fmtCapacity, fmtPopulation } from "@/lib/infra";

// "Assets at Risk" — the exposure readout attached to a hazard anomaly.
// variant="compact" (ledger card / alert row) or "full" (detail drawer).
export const ExposureBlock = ({ exposure, variant = "compact", testId }) => {
  if (!exposure) return null;
  const {
    power_plants = 0,
    cities = 0,
    capacity_mw = 0,
    population = 0,
    radius_km,
    top_plants = [],
    top_cities = [],
  } = exposure;
  const none = power_plants === 0 && cities === 0;

  if (variant === "compact") {
    return (
      <div
        data-testid={testId}
        className="mt-2 border-l-2 border-amber-400/60 bg-amber-400/5 pl-2.5 py-1.5 pr-2"
      >
        <div className="flex items-center justify-between mb-1">
          <span className="text-[8px] font-mono uppercase tracking-widest text-amber-400/80 flex items-center gap-1">
            <ShieldAlert className="w-3 h-3" /> Assets at risk
          </span>
          <span className="text-[8px] font-mono text-amber-400/80">{radius_km != null ? `≤${Math.round(radius_km)}km` : ""}</span>
        </div>
        {none ? (
          <div className="text-[10px] font-mono text-atlas-secondary/75">
            No mapped grid or population within {Math.round(radius_km)} km
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1 text-[11px] font-mono text-amber-100">
              <Zap className="w-3 h-3 text-amber-400" />
              {power_plants} {power_plants === 1 ? "plant" : "plants"}
              <span className="text-amber-400/60">· {fmtCapacity(capacity_mw)}</span>
            </span>
            <span className="flex items-center gap-1 text-[11px] font-mono text-sky-100">
              <Users className="w-3 h-3 text-sky-400" />
              ~{fmtPopulation(population)}
            </span>
          </div>
        )}
      </div>
    );
  }

  // full
  return (
    <div className="mb-4 border border-amber-400/30 bg-amber-400/[0.04] p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-2">
        <ShieldAlert className="w-3.5 h-3.5 text-amber-400" />
        <span className="text-[10px] uppercase tracking-widest text-amber-400/80 font-mono">
          Assets at Risk · within {Math.round(radius_km)} km
        </span>
      </div>
      {none ? (
        <div className="text-[11px] font-mono text-atlas-secondary/85">
          No mapped power grid or population centres inside the impact radius.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div className="border border-amber-400/20 bg-amber-400/[0.04] px-2.5 py-2">
              <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-amber-400/70">
                <Zap className="w-3 h-3" /> Power grid
              </div>
              <div className="text-lg font-mono text-amber-100 leading-tight mt-0.5">{power_plants}</div>
              <div className="text-[10px] font-mono text-amber-400/60">{fmtCapacity(capacity_mw)} capacity</div>
            </div>
            <div className="border border-sky-400/20 bg-sky-400/[0.04] px-2.5 py-2">
              <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-sky-400/70">
                <Users className="w-3 h-3" /> Population
              </div>
              <div className="text-lg font-mono text-sky-100 leading-tight mt-0.5">~{fmtPopulation(population)}</div>
              <div className="text-[10px] font-mono text-sky-400/60">{cities} population centre{cities === 1 ? "" : "s"}</div>
            </div>
          </div>
          {top_plants.length > 0 && (
            <div className="mb-1.5">
              <div className="text-[9px] font-mono uppercase tracking-widest text-atlas-secondary/75 mb-0.5">
                Largest plants in radius
              </div>
              {top_plants.map((p, i) => (
                <div key={i} className="flex justify-between text-[10px] font-mono text-atlas-foreground/85 py-0.5">
                  <span className="truncate pr-2">{p.name}</span>
                  <span className="text-amber-400/80 shrink-0">
                    {fmtCapacity(p.capacity_mw)} · {p.km}km
                  </span>
                </div>
              ))}
            </div>
          )}
          {top_cities.length > 0 && (
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-atlas-secondary/75 mb-0.5">
                Population centres in radius
              </div>
              {top_cities.map((c, i) => (
                <div key={i} className="flex justify-between text-[10px] font-mono text-atlas-foreground/85 py-0.5">
                  <span className="truncate pr-2">{c.name}</span>
                  <span className="text-sky-400/80 shrink-0">
                    ~{fmtPopulation(c.population)} · {c.km}km
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
